import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Tag, Card } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import { segmentsApi } from '../services/api';

const { TextArea } = Input;

const Segments = () => {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await segmentsApi.list({ limit: 100 });
      setSegments(response.data);
    } catch (error) { 
      message.error('세그먼트 로드 실패'); 
    }
    finally { setLoading(false); }
  };

  const handleSave = async (values) => {
    try {
      let conditions = {};
      if (values.conditions) {
        try { 
          conditions = JSON.parse(values.conditions); 
        } catch { 
          message.error('JSON 형식 오류'); 
          return; 
        }
      }
      const data = { ...values, conditions };
      if (selectedSegment) {
        await segmentsApi.update(selectedSegment.id, data);
      } else {
        await segmentsApi.create(data);
      }
      message.success('저장되었습니다'); 
      setIsModalOpen(false); 
      form.resetFields(); 
      setSelectedSegment(null); 
      fetchData();
    } catch (error) { 
      message.error('저장 실패'); 
    }
  };

  const handleDelete = async (id) => { 
    try { 
      await segmentsApi.delete(id); 
      message.success('삭제되었습니다'); 
      fetchData(); 
    } catch (error) { 
      message.error('삭제 실패'); 
    } 
  };

  const handleRefresh = async (id) => { 
    try { 
      await segmentsApi.recalculate(id); 
      message.success('새로고침 완료'); 
      fetchData(); 
    } catch (error) { 
      message.error('새로고침 실패'); 
    } 
  };

  const openEditModal = (segment) => {
    setSelectedSegment(segment);
    form.setFieldsValue({
      name: segment.name,
      description: segment.description,
      conditions: JSON.stringify(segment.conditions, null, 2)
    });
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setSelectedSegment(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const columns = [
    { 
      title: '세그먼트명', 
      dataIndex: 'name', 
      key: 'name',
      render: (text, record) => (
        <a onClick={() => openEditModal(record)}>{text}</a>
      )
    },
    { 
      title: '유형', 
      dataIndex: 'is_dynamic', 
      key: 'type',
      render: (isDynamic) => (
        <Tag color={isDynamic ? 'blue' : 'green'}>
          {isDynamic ? '동적' : '정적'}
        </Tag>
      )
    },
    { 
      title: '대상 수', 
      dataIndex: 'cached_count', 
      key: 'count',
      render: (count) => (
        <Space>
          <TeamOutlined />
          {count?.toLocaleString() || 0}명
        </Space>
      )
    },
    {
      title: '작업',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            size="small"
            onClick={() => handleRefresh(record.id)}
          />
          <Button 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => openEditModal(record)}
          />
          <Popconfirm
            title="정말 삭제하시겠습니까?"
            onConfirm={() => handleDelete(record.id)}
            okText="삭제"
            cancelText="취소"
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div className="page-header">
        <h1>세그먼트 관리</h1>
      </div>

      <Card 
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            새 세그먼트
          </Button>
        }
      >
        <Table 
          columns={columns} 
          dataSource={segments} 
          rowKey="id" 
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={selectedSegment ? '세그먼트 수정' : '새 세그먼트'}
        open={isModalOpen}
        onCancel={() => { setIsModalOpen(false); form.resetFields(); setSelectedSegment(null); }}
        onOk={() => form.submit()}
        okText="저장"
        cancelText="취소"
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item 
            name="name" 
            label="세그먼트명" 
            rules={[{ required: true, message: '이름을 입력하세요' }]}
          >
            <Input placeholder="예: VIP 사용자" />
          </Form.Item>
          <Form.Item name="description" label="설명">
            <Input placeholder="세그먼트 설명" />
          </Form.Item>
          <Form.Item 
            name="conditions" 
            label="조건 (JSON)"
            rules={[{ required: true, message: '조건을 입력하세요' }]}
          >
            <TextArea 
              rows={6} 
              placeholder='{"tags": ["real"], "platform": "real"}'
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Segments;
